import React, { lazy } from "react";
import { Navigate } from "react-router";

import { ArrowLeftRightFill, LineChartLine, Debts, AppsLine, Wallet3Line, BarChartGroupedFill, PieChart2Line, FoldersLine, CodeSSlashFill, NewspaperLine, PulseLine, KeyLine, FileHistoryLine, MapPin2Line, Settings4Line, UserSettingsLine, PaletteLine, Building2Line, ShieldUserLine, VerifiedFill, Notification2Line, FileWord2Line, GroupLine, ShareLine, TransferMoney, ReceiptLine } from "@datanomik/core-ui/icons";
import { SettingsMenuLayout } from "@/layouts/SettingsMenu";
import { CashPoolingIcon } from "@/features/cash-pooling/shared/CashPoolingIcon";
import { BankIntegrationCredentialIcon } from "@/features/bank-integrations/shared/icons/BankIntegrationCredentialIcon";
import { FinancialClosingIcon } from "@/features/financial-closing/shared/FinancialClosingIcon";
import {
  OPERATOR_ROLE,
  FINANCE_ROLE,
  DEVELOPER_ROLE,
  READ_ONLY_ROLE,
  PAYMENT_CONTROLLER_ROLE,
  VIEW_FINANCE_ROLE,
  TRANSACTIONS_ROLE,
  PAYABLES_ROLE,
  ADMIN_ROLE,
  GENERAL_ADMIN_ROLE,
  USER_ROLE,
} from "./AppConstants";
import { getCashPoolingRoutes } from "../features/cash-pooling/routes";
import { getTariffsRoutes } from "../features/tariffs/routes";
import { getCashFlowsV2Routes } from "../features/cash-flows/routes";
import { getDebtsRoutes } from "../features/debts/routes";
import { DEBT_MODULE_TASK_IDS } from "../features/debts/shared/debtModules";
import { getPoliciesRoutes } from "../features/policies/routes";

const CashFlowsPageV2 = lazy(() =>
  import("../features/cash-flows/CashFlowsPage").then((m) => ({
    default: m.default,
  })),
);

const BankIntegrationsPage = lazy(() =>
  import("@/features/bank-integrations/BankIntegrationsPage").then((m) => ({
    default: m.BankIntegrationsPage,
  })),
);

const AppearancePage = lazy(() =>
  import("@/features/settings-appearance").then((m) => ({
    default: m.AppearancePage,
  })),
);

const BillingSettingsPage = lazy(() =>
  import("@/layouts/SettingsMenu/BillingSettingsPage").then((m) => ({
    default: m.BillingSettingsPage,
  })),
);

const IntegrationsPage = lazy(() =>
  import("@/features/settings-integrations").then((m) => ({
    default: m.IntegrationsPage,
  })),
);

const Home = lazy(() => import("../views/modules/Home"));
const Logs = lazy(() => import("../views/modules/Logs"));
const DevelopersTabs = lazy(
  () => import("../features/developers/DevelopersTabs"),
);
const McpManagement = lazy(() => import("../views/modules/McpManagement"));
const Profile = lazy(() => import("../views/modules/Profile"));
const Company = lazy(() => import("../views/modules/Company"));
const Investments = lazy(() => import("../views/modules_v2/Investments"));
const CoePortfolio = lazy(
  () =>
    import("../views/modules_v2/Investments/tabs/InvestmentsPortfolio/portfolios/CoePortfolio"),
);
const FundPortfolio = lazy(
  () =>
    import("../views/modules_v2/Investments/tabs/InvestmentsPortfolio/portfolios/FundPortfolio"),
);
const DepositPortfolio = lazy(
  () =>
    import("../views/modules_v2/Investments/tabs/InvestmentsPortfolio/portfolios/DepositPortfolio"),
);
const CommittedPortfolio = lazy(
  () =>
    import("../views/modules_v2/Investments/tabs/InvestmentsPortfolio/portfolios/CommittedPortfolio"),
);
const DerivativePortfolio = lazy(
  () =>
    import("../views/modules_v2/Investments/tabs/InvestmentsPortfolio/portfolios/DerivativePortfolio"),
);

const RETIRED_OVERVIEW_PATHS = [
  { key: "overview-modern-moved", path: "modern", to: ".." },
  { key: "overview-accounts-moved", path: "accounts", to: ".." },
  { key: "overview-strategic-moved", path: "strategic", to: ".." },
  { key: "overview-analytic-moved", path: "analytic", to: ".." },
  { key: "overview-old-moved", path: "old", to: ".." },
  { key: "overview-old-strategic-moved", path: "old/strategic", to: "../.." },
  { key: "overview-old-analytic-moved", path: "old/analytic", to: "../.." },
];

const Overview = lazy(() => import("../views/modules/Overview"));
const OverviewEntry = lazy(() =>
  import("../modern/overview/OverviewEntry").then((m) => ({
    default: m.OverviewEntry,
  })),
);
const WidgetLab = lazy(() =>
  import("../modern/widgets/lab/WidgetLabPage").then((m) => ({
    default: m.WidgetLabPage,
  })),
);
const ViewLink = lazy(() =>
  import("../features/links/link-detail/LinkDetailPage").then((m) => ({
    default: m.LinkDetailPage,
  })),
);
const LinkApis = lazy(() =>
  import("../features/links/link-apis").then((m) => ({
    default: m.LinkApisPage,
  })),
);
const TransactionsTabs = lazy(
  () => import("../views/modules/Transactions/TransactionsTabs"),
);
const NotificationsMod = lazy(() => import("../views/modules/Notifications"));
const Rules = lazy(() => import("../views/modules/Notifications/Rules"));
const RulesCreate = lazy(
  () => import("../views/modules/Notifications/Rules/RulesCreate"),
);
const ViewAccount = lazy(() =>
  import("../features/accounts/account-details/AccountDetailsPage").then((m) => ({
    default: m.AccountDetailsPage,
  })),
);
const CategoryCreate = lazy(
  () => import("../views/modules/Categories/CategoryCreate"),
);
const Users = lazy(() => import("../views/modules/Users"));
const UsersTabs = lazy(() => import("../views/modules/Users/UsersTabs"));
const CustomRoleForm = lazy(() => import("../views/modules/CustomRoles/RoleForm"));
const LegacyRolesRedirect = lazy(() => import("../views/modules/CustomRoles/LegacyRedirect"));
const SchedulesRedirect = lazy(
  () => import("@/features/sync-schedules/SchedulesRedirect"),
);
// Resolves the stable /go/liability/:type/:id link openbanking-core's LiabilityDeepLinkFactory
// builds (covenant BREACHED email today) into whichever debts/mutuos/securitizations route
// currently serves that contract -- see the component's own docblock.
const LiabilityRedirectPage = lazy(
  () => import("@/features/debts/liability-redirect/LiabilityRedirectPage"),
);
const BalanceTabs = lazy(() => import("../views/modules/Balances/BalanceTabs"));
const LinkTabs = lazy(() => import("../views/modules/Links/LinkTabs"));
const GeneralOwners = lazy(() => import("../views/modules/GeneralOwners"));
const Intercompany = lazy(() => import("../features/intercompany/IntercompanyPage"));
const GeneralOwnersReassignOwners = lazy(
  () => import("../views/modules/GeneralOwners/ReassignOwners"),
);
const ReportsRoute = lazy(() => import("../layouts/reports/ReportsRoute"));
const StatementsMyExtractsPage = lazy(
  () => import("../views/modules/Statements/MyExtracts/Page"),
);
const _OptimizationWithdrawals = lazy(
  () => import("../views/modules/Optimization/Withdrawals"),
);
const Security = lazy(() => import("../views/modules/Security"));
const CashFlowCategories = lazy(
  () => import("../views/modules/CashFlowCategories"),
);
const CategoryRulesCreate = lazy(
  () => import("../views/modules/Categories/CategoryRules/RulesCreate"),
);
const ConsolidatedBalances = lazy(
  () => import("../views/modules/ConsolidatedBalances"),
);
const WorkspaceViewActions = lazy(
  () => import("../views/modules/WorkspaceViews/WorkspaceViewActions"),
);
const OnboardingWelcome = lazy(() => import("../views/modules/Onboarding"));
const OnboardingTabs = lazy(() => import("../views/modules/Onboarding/Tabs"));
const WorkspaceTabs = lazy(() => import("../views/modules/Workspaces/tabs"));
const FinancialClosingPage = lazy(
  () => import("../features/financial-closing/FinancialClosingPage"),
);
const ClosingPageLayout = lazy(
  () => import("../features/financial-closing/ClosingPageLayout"),
);
const ClosingStatementsPage = lazy(
  () => import("../features/financial-closing/ClosingStatementsPage"),
);
const CashPoolingPage = lazy(
  () => import("../features/cash-pooling/CashPoolingPage"),
);
const DebtsPage = lazy(
  () => import("../features/debts/DebtsPage"),
);
const TariffsPage = lazy(
  () => import("../features/tariffs/TariffsPage"),
);
const PoliciesPage = lazy(
  () => import("../features/policies/PoliciesPage"),
);
const LegacyPoliciesRedirect = lazy(() =>
  import("../features/policies/LegacyPoliciesRedirect").then((m) => ({
    default: m.LegacyPoliciesRedirect,
  })),
);
const ChatPage = lazy(() =>
  import("../modern/chat/ChatPage").then((m) => ({ default: m.ChatPage })),
);

const ONBOARDING_MENU_ITEMS = [
  {
    key: "onboarding",
    path: "onboarding",
    label: "Onboarding",
    showInMenu: false,
    type: "workspace",
    children: [
      {
        key: "onboarding",
        path: "",
        label: "Onboarding",
        element: <OnboardingWelcome />,
        showInMenu: false,
        type: "workspace",
      },
      {
        key: "onboarding-users-profile",
        path: "users/profile",
        label: "Users",
        element: <OnboardingTabs />,
        showInMenu: false,
        type: "workspace",
      },
      {
        key: "onboarding-users-add-users",
        path: "users/add-users",
        label: "Users",
        element: <OnboardingTabs />,
        showInMenu: false,
        type: "workspace",
      },
      {
        key: "onboarding-institutions",
        path: "institutions",
        label: "Banking institutions",
        element: <OnboardingTabs />,
        showInMenu: false,
        type: "workspace",
      },
    ],
  },
];
export const MAIN_MENU_ITEMS = [
  {
    key: "rules",
    label: "Rules",
    path: "rules",
    showInMenu: false,
    type: "workspace",
    children: [
      {
        key: "rules",
        path: "",
        element: <Rules />,
        showInMenu: false,
        type: "workspace",
      },
      {
        key: "rules",
        path: "create",
        element: <RulesCreate />,
        showInMenu: false,
        type: "workspace",
      },
      {
        key: "rules",
        path: "edit/:ruleId",
        element: <RulesCreate />,
        showInMenu: false,
        type: "workspace",
      },
    ],
    actions: {
      view: [OPERATOR_ROLE, FINANCE_ROLE],
      create: [OPERATOR_ROLE, FINANCE_ROLE],
      edit: [OPERATOR_ROLE, FINANCE_ROLE],
      delete: [OPERATOR_ROLE, FINANCE_ROLE],
    },
  },
  {
    taskId: 1000,
    key: "overview-general",
    icon: <LineChartLine />,
    label: "Overview",
    description: "Follow your company's all financial position",
    path: "overview",
    showInMenu: true,
    withView: true,
    type: "workspace",
    children: [
      {
        taskParentId: 1000,
        key: "overview-home",
        path: "",
        label: "Overview",
        element: <OverviewEntry />,
        isBeta: true,
        showInMenu: false,
        hideBackButton: true,
        withView: true,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
        },
      },
      {
        taskId: 1001,
        taskParentId: 1000,
        key: "overview-widget-lab",
        path: "widget-lab",
        label: "Widget lab",
        element: <WidgetLab />,
        isBeta: true,
        showInMenu: false,
        withView: true,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
        },
      },
      {
        taskId: 1100,
        taskParentId: 1000,
        key: "overview-accounts",
        path: "old/accounts",
        label: "Accounts",
        element: <Overview />,
        showInMenu: false,
        hideBackButton: true,
        withView: true,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
        },
      },
      ...RETIRED_OVERVIEW_PATHS.map(({ key, path, to }) => ({
        taskParentId: 1000,
        key,
        path,
        element: <Navigate to={to} relative="path" replace />,
        showInMenu: false,
        withView: true,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
        },
      })),
    ],
    actions: {
      view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
    },
  },

  {
    key: "divider-group-1",
    type: "divider",
    showInMenu: true,
  },
  {
    taskId: 1300,
    key: "links",
    icon: <AppsLine />,
    label: "Links e Contas",
    description: "Follow the status of all your bank connections",
    path: "links",
    showInMenu: true,
    type: "workspace",
    sidebarChildren: [
      { key: "links", path: "", label: "Links", taskId: 1300 },
      { key: "links-accounts", path: "accounts", label: "Contas", taskId: 1400 },
    ],
    children: [
      {
        key: "links",
        path: "",
        element: <LinkTabs />,
        showInMenu: false,
        hideBackButton: true,
        type: "workspace",
      },
      {
        taskId: 1400,
        taskParentId: 1300,
        key: "links-accounts",
        path: "accounts",
        label: "Accounts",
        element: <LinkTabs />,
        showInMenu: false,
        hideBackButton: true,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
          create: [OPERATOR_ROLE, FINANCE_ROLE],
          edit: [OPERATOR_ROLE, FINANCE_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE],
        },
      },
      {
        key: "links",
        path: "view/:id",
        element: <ViewLink />,
        showInMenu: false,
        type: "workspace",
        hiddenPageTitle: true,
      },
      {
        key: "links-apis",
        path: "apis/:id",
        element: <LinkApis />,
        showInMenu: false,
        type: "workspace",
        hiddenPageTitle: true,
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
          create: [OPERATOR_ROLE, FINANCE_ROLE],
          edit: [OPERATOR_ROLE, FINANCE_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE],
        },
      },
      {
        key: "links-schedules",
        path: "schedules/*",
        label: "View schedules",
        showInMenu: false,
        type: "workspace",
        element: <SchedulesRedirect />,
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
          create: [OPERATOR_ROLE, FINANCE_ROLE],
          edit: [OPERATOR_ROLE, FINANCE_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE],
        },
      },
    ],
    actions: {
      view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
      create: [OPERATOR_ROLE, FINANCE_ROLE],
      edit: [OPERATOR_ROLE, FINANCE_ROLE],
      delete: [OPERATOR_ROLE, FINANCE_ROLE],
    },
  },
  {
    taskId: 1500,
    key: "accounts",
    icon: <Wallet3Line />,
    label: "Cash position",
    description:
      "Clear and up-to-date view of cash positions for strategic financial management",
    path: "accounts",
    showInMenu: true,
    type: "workspace",
    withView: true,
    sidebarChildren: [
      { key: "accounts", path: "", label: "Posição de Caixa", taskId: 1500 },
      { key: "accounts-forecast", path: "forecast", label: "Forecast", taskId: 1600 },
      { key: "accounts-history", path: "history", label: "Evolução de Saldo", taskId: 1700 },
      { key: "accounts-manual", path: "accounts-manual", label: "Posições Manuais", taskId: 1504 },
    ],
    children: [
      {
        key: "accounts",
        path: "",
        hideBackButton: true,
        element: <BalanceTabs />,
        showInMenu: false,
        type: "workspace",
        withView: true,
      },
      {
        taskId: 1600,
        taskParentId: 1500,
        key: "accounts-forecast",
        path: "forecast",
        hideBackButton: true,
        element: <BalanceTabs />,
        label: "Forecast",
        showInMenu: false,
        type: "workspace",
        withView: true,
        actions: {
          view: [
            OPERATOR_ROLE,
            FINANCE_ROLE,
            READ_ONLY_ROLE,
            VIEW_FINANCE_ROLE,
          ],
          edit: [OPERATOR_ROLE, FINANCE_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE],
          export: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
        },
      },
      {
        taskId: 1700,
        taskParentId: 1500,
        key: "accounts-history",
        path: "history",
        label: "Balance Evolution",
        hideBackButton: true,
        element: <BalanceTabs />,
        showInMenu: false,
        type: "workspace",
        withView: true,
        actions: {
          view: [
            OPERATOR_ROLE,
            FINANCE_ROLE,
            READ_ONLY_ROLE,
            VIEW_FINANCE_ROLE,
          ],
          edit: [OPERATOR_ROLE, FINANCE_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE],
          export: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
        },
      },
      {
        taskId: 1700,
        taskParentId: 1500,
        key: "accounts-consolidated",
        path: "consolidated",
        label: "Consolidated view by Owner",
        element: <ConsolidatedBalances />,
        showInMenu: false,
        type: "workspace",
        withView: true,
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
          edit: [OPERATOR_ROLE, FINANCE_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE],
        },
      },
      {
        taskId: 1504,
        taskParentId: 1500,
        key: "accounts-manual",
        path: "accounts-manual",
        label: "Manual positions",
        hideBackButton: true,
        element: <BalanceTabs />,
        showInMenu: false,
        type: "workspace",
        withView: true,
        actions: {
          view: [
            OPERATOR_ROLE,
            FINANCE_ROLE,
            READ_ONLY_ROLE,
            VIEW_FINANCE_ROLE,
          ],
          create: [OPERATOR_ROLE, FINANCE_ROLE],
          edit: [OPERATOR_ROLE, FINANCE_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE],
        },
      },
      {
        taskId: 1505,
        taskParentId: 1500,
        key: "account-detail",
        path: "view/:accountId",
        element: <ViewAccount />,
        showInMenu: false,
        type: "workspace",
        withView: true,
        hiddenPageTitle: true,
        hideBackButton: true,
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
          edit: [OPERATOR_ROLE, FINANCE_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE],
          export: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
        },
      },
    ],
    actions: {
      view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE, VIEW_FINANCE_ROLE],
      edit: [OPERATOR_ROLE, FINANCE_ROLE],
      delete: [OPERATOR_ROLE, FINANCE_ROLE],
      export: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
    },
  },
  {
    taskId: 2200,
    key: "cash-flows",
    icon: <PieChart2Line />,
    label: "Cash flow",
    path: "cash-flows",
    showInMenu: true,
    withView: true,
    type: "workspace",
    hideBackButton: true,
    element: <CashFlowsPageV2 />,
    sidebarChildren: [
      { key: "cash-flows", path: "", label: "Fluxo de Caixa", taskId: 2200 },
      { key: "settings-cash-flows", fullPath: "cash-flows/settings/categories", label: "Configurações", taskId: 2200 },
    ],
    actions: {
      view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
      edit: [OPERATOR_ROLE, FINANCE_ROLE],
      create: [OPERATOR_ROLE, FINANCE_ROLE],
      export: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
    },
    children: getCashFlowsV2Routes().map((route) => ({
      ...route,
      showInMenu: false,
      hideBackButton: true,
      withView: true,
      type: "workspace",
      taskId: 2200,
      actions: {
        view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
        edit: [OPERATOR_ROLE, FINANCE_ROLE],
        create: [OPERATOR_ROLE, FINANCE_ROLE],
        export: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
      },
    })),
  },
  {
    taskId: 2200,
    key: "settings-cash-flows",
    icon: <PieChart2Line />,
    label: "Cashflow Configuration",
    path: "cash-flows/settings",
    showInMenu: false,
    children: [
      {
        taskId: 2200,
        key: "groups-cash-flows",
        label: "Group of categories",
        path: "groups",
        showInMenu: false,
        type: "workspace",
        element: <CashFlowCategories />,
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
          create: [OPERATOR_ROLE, FINANCE_ROLE],
          edit: [OPERATOR_ROLE, FINANCE_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE],
        },
      },
      {
        key: "sections-cash-flows",
        label: "Sections",
        path: "sections",
        showInMenu: false,
        type: "workspace",
        element: <CashFlowCategories />,
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
          create: [OPERATOR_ROLE, FINANCE_ROLE],
          edit: [OPERATOR_ROLE, FINANCE_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE],
        },
      },
      {
        taskId: 2100,
        taskParentId: 2000,
        key: "categories",
        label: "Categories",
        path: "categories",
        showInMenu: false,
        type: "workspace",
        children: [
          {
            key: "categories",
            path: "",
            element: <CashFlowCategories />,
            showInMenu: false,
            type: "workspace",
            actions: {
              view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
              create: [OPERATOR_ROLE, FINANCE_ROLE],
              edit: [OPERATOR_ROLE, FINANCE_ROLE],
              delete: [OPERATOR_ROLE, FINANCE_ROLE],
            },
          },
          {
            key: "categories-create",
            path: "create",
            label: "New category",
            element: <CategoryCreate />,
            showInMenu: false,
            type: "workspace",
            actions: {
              view: [OPERATOR_ROLE, FINANCE_ROLE],
              create: [OPERATOR_ROLE, FINANCE_ROLE],
              edit: [OPERATOR_ROLE, FINANCE_ROLE],
              delete: [OPERATOR_ROLE, FINANCE_ROLE],
            },
          },
          {
            key: "categories-edit",
            path: "edit/:categoryId",
            element: <CategoryCreate />,
            showInMenu: false,
            type: "workspace",
            actions: {
              view: [OPERATOR_ROLE, FINANCE_ROLE],
              create: [OPERATOR_ROLE, FINANCE_ROLE],
              edit: [OPERATOR_ROLE, FINANCE_ROLE],
              delete: [OPERATOR_ROLE, FINANCE_ROLE],
            },
          },
          {
            key: "categories-edit-rules-create",
            path: "edit/:categoryId/rules/create",
            label: "Create new rule",
            element: <CategoryRulesCreate />,
            showInMenu: false,
            type: "workspace",
            actions: {
              view: [OPERATOR_ROLE, FINANCE_ROLE],
              create: [OPERATOR_ROLE, FINANCE_ROLE],
              edit: [OPERATOR_ROLE, FINANCE_ROLE],
              delete: [OPERATOR_ROLE, FINANCE_ROLE],
            },
          },
          {
            key: "categories-edit-rules-edit",
            path: "edit/:categoryId/rules/edit/:ruleId",
            label: "Edit rule",
            element: <CategoryRulesCreate />,
            showInMenu: false,
            type: "workspace",
            actions: {
              view: [OPERATOR_ROLE, FINANCE_ROLE],
              create: [OPERATOR_ROLE, FINANCE_ROLE],
              edit: [OPERATOR_ROLE, FINANCE_ROLE],
              delete: [OPERATOR_ROLE, FINANCE_ROLE],
            },
          },
        ],
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
          create: [OPERATOR_ROLE, FINANCE_ROLE],
          edit: [OPERATOR_ROLE, FINANCE_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE],
        },
      },
    ],
  },
  {
    taskId: 1817,
    key: "investments-performance-v2",
    icon: <BarChartGroupedFill />,
    label: "Investments",
    description: "Follow all about your investments",
    path: "v2/investments/performance",
    showInMenu: false,
    withView: false,
    hideBackButton: true,
    type: "workspace",
    element: <Investments />,
    actions: {
      view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
    },
  },
  {
    taskId: 1818,
    key: "investments-movements-v2",
    icon: <BarChartGroupedFill />,
    label: "Investments",
    description: "Follow all about your investments",
    path: "v2/investments/movements",
    showInMenu: false,
    withView: false,
    hideBackButton: true,
    type: "workspace",
    element: <Investments />,
    actions: {
      view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
    },
  },
  {
    taskId: 1816,
    key: "investments-v2",
    icon: <BarChartGroupedFill />,
    label: "Investments",
    description: "Follow your portfolio",
    path: "v2/investments/portfolio",
    showInMenu: true,
    withView: false,
    hideBackButton: true,
    type: "workspace",
    sidebarChildren: [
      { key: "investments-v2", fullPath: "v2/investments/portfolio", label: "Portfolio", taskId: 1816 },
      { key: "investments-performance-v2", fullPath: "v2/investments/performance", label: "Performance", taskId: 1817 },
      { key: "investments-movements-v2", fullPath: "v2/investments/movements", label: "Movimentos", taskId: 1818 },
    ],
    children: [
      {
        key: "investments-v2",
        path: "",
        element: <Investments />,
        hideBackButton: true,
        showInMenu: false,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
        },
      },
      {
        hiddenPageTitle: true,
        key: "investments-portfolio-deposit",
        taskId: 1816,
        path: "deposit",
        element: <DepositPortfolio />,
        showInMenu: false,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
        },
      },
      {
        hiddenPageTitle: true,
        key: "investments-portfolio-fund",
        taskId: 1816,
        path: "fund",
        element: <FundPortfolio />,
        showInMenu: false,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
        },
      },
      {
        hiddenPageTitle: true,
        key: "investments-portfolio-coe",
        taskId: 1816,
        path: "coe",
        element: <CoePortfolio />,
        showInMenu: false,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
        },
      },
      {
        hiddenPageTitle: true,
        key: "investments-portfolio-committed",
        taskId: 1816,
        path: "committed",
        element: <CommittedPortfolio />,
        showInMenu: false,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
        },
      },
      {
        hiddenPageTitle: true,
        key: "investments-portfolio-derivative",
        taskId: 1816,
        path: "derivative",
        element: <DerivativePortfolio />,
        showInMenu: false,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
        },
      },
    ],
    actions: {
      view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
      edit: [OPERATOR_ROLE, FINANCE_ROLE],
    },
  },
  {
    key: "divider-group-2",
    type: "divider",
    showInMenu: true,
  },
  {
    taskId: 2000,
    key: "transactions",
    icon: <ArrowLeftRightFill />,
    label: "Transactions",
    description: "Follow your financial transactions",
    path: "transactions",
    showInMenu: true,
    withView: true,
    type: "workspace",
    sidebarChildren: [
      { key: "transactions-list", path: "", label: "Transactions", taskId: 2000 },
      { key: "pix-transactions", path: "pix", label: "Pix", taskId: 2004 },
      { key: "transactions-payments", path: "payments", label: "Payments to suppliers", taskId: 2001 },
      { key: "payment-slips", path: "slips", label: "Payment slip", taskId: 2006 },
      { key: "exchange-operations", path: "exchange", label: "Exchange", taskId: 2007 },
      // Gated by the Tariffs task, exactly like the Tarifas module — fee data is fee data, wherever it is read.
      { key: "fees-transactions", path: "tarifas", label: "Tariffs", taskId: 5300 },
    ],
    children: [
      {
        key: "transactions-list",
        path: "",
        hideBackButton: true,
        element: <TransactionsTabs />,
        withView: true,
        showInMenu: false,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE, PAYMENT_CONTROLLER_ROLE, VIEW_FINANCE_ROLE, TRANSACTIONS_ROLE],
        },
      },
      {
        taskId: 2004,
        key: "pix-transactions",
        path: "pix",
        label: "Pix",
        hideBackButton: true,
        element: <TransactionsTabs />,
        withView: true,
        showInMenu: false,
        type: "workspace",
        actions: {
          view: [
            OPERATOR_ROLE,
            FINANCE_ROLE,
            READ_ONLY_ROLE,
            PAYMENT_CONTROLLER_ROLE,
            VIEW_FINANCE_ROLE,
            TRANSACTIONS_ROLE,
          ],
          export: [OPERATOR_ROLE, FINANCE_ROLE, TRANSACTIONS_ROLE],
        },
      },
      {
        taskId: 2001,
        key: "transactions-payments",
        path: "payments",
        label: "Payments to suppliers",
        hideBackButton: true,
        element: <TransactionsTabs />,
        withView: true,
        showInMenu: false,
        type: "workspace",
        actions: {
          view: [
            OPERATOR_ROLE,
            FINANCE_ROLE,
            READ_ONLY_ROLE,
            PAYMENT_CONTROLLER_ROLE,
            VIEW_FINANCE_ROLE,
            TRANSACTIONS_ROLE,
            PAYABLES_ROLE,
          ],
          export: [OPERATOR_ROLE, FINANCE_ROLE, TRANSACTIONS_ROLE, PAYABLES_ROLE],
          create: [OPERATOR_ROLE, FINANCE_ROLE, TRANSACTIONS_ROLE, PAYABLES_ROLE],
          edit: [OPERATOR_ROLE, FINANCE_ROLE, TRANSACTIONS_ROLE, PAYABLES_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE, TRANSACTIONS_ROLE, PAYABLES_ROLE],
        },
      },
      {
        taskId: 2003,
        key: "transactions-payments-receipt",
        path: "payments/receipt",
        label: "Payments receipts",
        hideBackButton: true,
        element: <Navigate to=".." relative="path" replace />,
        withView: true,
        showInMenu: false,
        type: "workspace",
        actions: {
          view: [
            OPERATOR_ROLE,
            FINANCE_ROLE,
            READ_ONLY_ROLE,
            PAYMENT_CONTROLLER_ROLE,
            VIEW_FINANCE_ROLE,
            TRANSACTIONS_ROLE,
            PAYABLES_ROLE,
          ],
          export: [OPERATOR_ROLE, FINANCE_ROLE, TRANSACTIONS_ROLE, PAYABLES_ROLE],
          create: [OPERATOR_ROLE, FINANCE_ROLE, TRANSACTIONS_ROLE, PAYABLES_ROLE],
          edit: [OPERATOR_ROLE, FINANCE_ROLE, TRANSACTIONS_ROLE, PAYABLES_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE, TRANSACTIONS_ROLE, PAYABLES_ROLE],
        },
      },
      {
        taskId: 2006,
        key: "payment-slips",
        path: "slips",
        label: "Payment Slips",
        hideBackButton: true,
        element: <TransactionsTabs />,
        withView: true,
        showInMenu: false,
        type: "workspace",
        actions: {
          view: [
            OPERATOR_ROLE,
            FINANCE_ROLE,
            READ_ONLY_ROLE,
            PAYMENT_CONTROLLER_ROLE,
            VIEW_FINANCE_ROLE,
            TRANSACTIONS_ROLE,
          ],
          export: [OPERATOR_ROLE, FINANCE_ROLE, TRANSACTIONS_ROLE],
        },
      },
      {
        taskId: 2007,
        key: "exchange-operations",
        path: "exchange",
        label: "Exchange",
        hideBackButton: true,
        element: <TransactionsTabs />,
        withView: true,
        showInMenu: false,
        type: "workspace",
        actions: {
          view: [
            OPERATOR_ROLE,
            FINANCE_ROLE,
            READ_ONLY_ROLE,
            PAYMENT_CONTROLLER_ROLE,
            VIEW_FINANCE_ROLE,
            TRANSACTIONS_ROLE,
          ],
          export: [OPERATOR_ROLE, FINANCE_ROLE, TRANSACTIONS_ROLE],
        },
      },
      {
        // Same gate as the Tarifas module (task 5300) instead of the Transactions one plus an internal-only
        // check: the tab reads the same standardized fees, so "who may see fees" is one answer, not two.
        taskId: 5300,
        key: "fees-transactions",
        path: "tarifas",
        label: "Tariffs",
        hideBackButton: true,
        element: <TransactionsTabs />,
        withView: true,
        showInMenu: false,
        type: "workspace",
        actions: {
          view: [
            OPERATOR_ROLE,
            FINANCE_ROLE,
            READ_ONLY_ROLE,
            PAYMENT_CONTROLLER_ROLE,
            VIEW_FINANCE_ROLE,
            TRANSACTIONS_ROLE,
          ],
          export: [OPERATOR_ROLE, FINANCE_ROLE, TRANSACTIONS_ROLE],
        },
      },
    ],
    actions: {
      view: [
        OPERATOR_ROLE,
        FINANCE_ROLE,
        READ_ONLY_ROLE,
        PAYMENT_CONTROLLER_ROLE,
        VIEW_FINANCE_ROLE,
        TRANSACTIONS_ROLE,
        PAYABLES_ROLE,
      ],
      export: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE, TRANSACTIONS_ROLE],
      create: [OPERATOR_ROLE, FINANCE_ROLE, TRANSACTIONS_ROLE, PAYABLES_ROLE],
    },
  },
  // {
  //   taskId: 2200,
  //   key: "cash-flows",
  //   icon: <PieChart2Line />,
  //   label: "Cash flow",
  //   path: "cash-flows",-
  //   showInMenu: true,
  //   type: "workspace",
  //   hideBackButton: true,
  //   element: <CashFlows />,
  //   actions: {
  //     view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
  //     edit: [OPERATOR_ROLE, FINANCE_ROLE],
  //     create: [OPERATOR_ROLE, FINANCE_ROLE],
  //     export: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
  //   },
  // },
  {
    key: "transfers",
    icon: <TransferMoney />,
    label: "Transfers",
    taskId: 8000,
    path: "cash-pooling",
    showInMenu: true,
    type: "workspace",
    sidebarChildren: [
      { key: "cash-pooling-overview", fullPath: "cash-pooling", label: "Overview", taskId: 8000 },
      { key: "cash-pooling-rules", fullPath: "cash-pooling", label: "Regras automáticas", taskId: 8000, queryParam: "tab=regras" },
      { key: "new-transfer", label: "Nova transferência", action: "open-direct-transfer", taskId: 2002 },
    ],
    actions: {
      view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
    },
  },

  {
    key: "cash-pooling",
    icon: <CashPoolingIcon />,
    taskId: 8000,
    label: "Cash Pooling",
    path: "cash-pooling",
    showInMenu: false,
    withView: true,
    type: "workspace",
    hideBackButton: true,
    element: <CashPoolingPage />,
    actions: {
      view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
    },
    children: getCashPoolingRoutes().map((route: Record<string, any>) => {
      const addRouteDefaults = (r) => ({
        ...r,
        showInMenu: false,
        hideBackButton: true,
        type: "workspace",
        withView: true,
        taskId: 8000,
        actions: r.actions ?? {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
        },
      });

      const baseRoute = addRouteDefaults(route);

      // If the route has children, apply the same properties to them recursively
      if (route.children) {
        baseRoute.children = route.children.map(addRouteDefaults);
      }

      return baseRoute;
    }),
  },
  {
    key: "divider-group-3",
    type: "divider",
    showInMenu: true,
  },
  {
    taskId: 4000,
    key: "reports",
    icon: <NewspaperLine />,
    label: "Reports",
    path: "reports",
    showInMenu: true,
    withView: true,
    type: "workspace",
    dynamicSubmenu: "reports",
    children: [
      {
        key: "reports",
        label: "New Report",
        path: "",
        hideBackButton: true,
        element: <ReportsRoute />,
        showInMenu: false,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE, VIEW_FINANCE_ROLE, TRANSACTIONS_ROLE],
        },
      },
      {
        taskId: 4000,
        key: "reports-scheduler",
        label: "Report schedules",
        path: "scheduler",
        hideBackButton: true,
        element: <ReportsRoute />,
        showInMenu: false,
        type: "workspace",
        withView: true,
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE, TRANSACTIONS_ROLE],
          create: [OPERATOR_ROLE, FINANCE_ROLE, TRANSACTIONS_ROLE],
          edit: [OPERATOR_ROLE, FINANCE_ROLE, TRANSACTIONS_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE, TRANSACTIONS_ROLE],
        },
      },
      {
        taskId: 4002,
        key: "reports-executions",
        label: "Executions",
        path: "executions",
        hideBackButton: true,
        element: <ReportsRoute />,
        showInMenu: false,
        type: "workspace",
        withView: true,
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE, TRANSACTIONS_ROLE],
          create: [OPERATOR_ROLE, FINANCE_ROLE, TRANSACTIONS_ROLE],
          edit: [OPERATOR_ROLE, FINANCE_ROLE, TRANSACTIONS_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE, TRANSACTIONS_ROLE],
        },
      },
      {
        key: "reports-schedule-detail",
        label: "Report schedules",
        path: "scheduler/:scheduleId",
        hideBackButton: true,
        element: <ReportsRoute />,
        showInMenu: false,
        type: "workspace",
        withView: true,
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE, TRANSACTIONS_ROLE],
          create: [OPERATOR_ROLE, FINANCE_ROLE, TRANSACTIONS_ROLE],
          edit: [OPERATOR_ROLE, FINANCE_ROLE, TRANSACTIONS_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE, TRANSACTIONS_ROLE],
        },
      },
      {
        key: "reports-detail",
        label: "Reports",
        path: ":taskId",
        hideBackButton: true,
        element: <ReportsRoute />,
        showInMenu: false,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE, VIEW_FINANCE_ROLE, TRANSACTIONS_ROLE],
          create: [OPERATOR_ROLE, FINANCE_ROLE, TRANSACTIONS_ROLE],
          edit: [OPERATOR_ROLE, FINANCE_ROLE, TRANSACTIONS_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE, TRANSACTIONS_ROLE],
        },
      },
    ],
    actions: {
      view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE, VIEW_FINANCE_ROLE, TRANSACTIONS_ROLE],
    },
  },
  {
    taskId: 5000,
    key: "bank-statements",
    path: "bank-statements",
    showInMenu: false,
    withView: true,
    type: "workspace",
    actions: {
      view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
    },
    children: [
      {
        key: "bank-statements",
        path: "",
        element: <Navigate to="../closing" relative="path" replace />,
        showInMenu: false,
        withView: true,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
        },
      },
      {
        taskId: 5001,
        key: "bank-statements-my-extracts",
        path: "my-extracts",
        element: <Navigate to="../../closing/my-extracts" relative="path" replace />,
        showInMenu: false,
        withView: true,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
        },
      },
    ],
  },
  {
    // Hidden from sidebar — replaced by the unified "Extratos & Fechamento"
    // entry (taskId 5000, /closing) below. Route stays registered so users
    // with only taskId 5002 can still reach the legacy page via direct URL.
    taskId: 5002,
    key: "financial-closing",
    icon: <FinancialClosingIcon />,
    label: "Fechamento financeiro",
    path: "financial-closing",
    showInMenu: false,
    withView: true,
    type: "workspace",
    hideBackButton: true,
    element: <FinancialClosingPage />,
    actions: {
      view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
    },
  },
  {
    // Unified Statements & Closing — accessible via the new TopNav AND
    // the legacy sidebar (single flat entry, tabs handle Statements vs
    // My extracts internally). Replaces the legacy bank-statements and
    // financial-closing sidebar entries, which are now hidden but kept
    // alive for direct-URL access.
    taskId: 5000,
    key: "closing",
    icon: <FoldersLine />,
    label: "Extratos & Fechamento",
    path: "closing",
    showInMenu: true,
    withView: true,
    type: "workspace",
    hideBackButton: true,
    element: <ClosingPageLayout />,
    sidebarChildren: [
      { key: "closing-statements", path: "", label: "Extratos", taskId: 5000 },
      {
        key: "closing-monthly", path: "", queryParam: "view=institution", label: "Fechamento Mensal", taskId: 5000,
      },
      {
        key: "closing-my-extracts", path: "my-extracts", label: "Extratos por Período", taskId: 5001,
      },
    ],
    actions: {
      view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
      create: [OPERATOR_ROLE, FINANCE_ROLE],
      edit: [OPERATOR_ROLE, FINANCE_ROLE],
      delete: [OPERATOR_ROLE, FINANCE_ROLE],
    },
    children: [
      {
        taskId: 5000,
        key: "closing-default",
        path: "",
        // Mirror the parent's icon and label so the Breadcrumbs header
        // renders "[FoldersLine] Extratos & Fechamento" on /closing —
        // matches the sidebar entry icon for visual consistency.
        icon: <FoldersLine />,
        label: "Statements & Closing",
        element: <ClosingStatementsPage />,
        showInMenu: false,
        hideBackButton: true,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
        },
      },
      {
        taskId: 5001,
        key: "closing-my-extracts",
        path: "my-extracts",
        label: "Meus Extratos",
        icon: <FileHistoryLine />,
        element: <StatementsMyExtractsPage />,
        showInMenu: false,
        hideBackButton: true,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
          create: [OPERATOR_ROLE, FINANCE_ROLE],
          edit: [OPERATOR_ROLE, FINANCE_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE],
        },
      },
    ],
  },

  {
    key: "divider-group-4",
    type: "divider",
    showInMenu: true,
  },
  {
    key: "debts",
    taskId: 5200,
    icon: <Debts />,
    label: "Dívidas",
    path: "debts",
    showInMenu: true,
    withView: true,
    isBeta: true,
    type: "workspace",
    hideBackButton: true,
    element: <DebtsPage />,
    sidebarChildren: [
      { key: "debts-overview", path: "", label: "Visão geral", taskId: 5200 },
      { key: "debts-my-debts", path: "my-debts", label: "Dívidas bancárias", taskId: 5200 },
      { key: "debts-new-debt", fullPath: "debts/new-debt", label: "Nova dívida", taskId: 5200 },
      { key: "debts-mutuos", fullPath: "debts/mutuos", label: "Intercompany", taskId: DEBT_MODULE_TASK_IDS.MUTUOS },
      {
        key: "debts-indexadores",
        path: "",
        queryParam: "view=indexadores&modo=consulta",
        label: "Consultar indexador",
        taskId: 5200,
      },
    ],
    actions: {
      view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
      create: [OPERATOR_ROLE, FINANCE_ROLE],
      edit: [OPERATOR_ROLE, FINANCE_ROLE],
      delete: [OPERATOR_ROLE, FINANCE_ROLE],
    },
    children: getDebtsRoutes().map((route) => ({
      ...route,
      showInMenu: false,
      hideBackButton: true,
      type: "workspace",
      withView: true,
      actions: {
        view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
      },
    })),
  },
  {
    // The one Tarifas entry, serving BOTH modules (getTariffsRoutes → TariffsModuleRoute): the rebuilt
    // module is what opens, and the previous one stays reachable from the module's settings gear until
    // the sunset in features/tariffs/context/version.constants. Gated by task 5300 only, as before.
    key: "tariffs",
    icon: <Wallet3Line />,
    taskId: 5300,
    label: "Tarifas",
    path: "tariffs",
    showInMenu: true,
    withView: true,
    isBeta: true,
    type: "workspace",
    hideBackButton: true,
    element: <TariffsPage />,
    actions: {
      view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
    },
    children: getTariffsRoutes().map((route) => ({
      ...route,
      showInMenu: false,
      hideBackButton: true,
      type: "workspace",
      withView: true,
      actions: {
        view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
      },
    })),
  },
  {
    key: "policies",
    icon: <VerifiedFill />,
    label: "policies.module.label",
    path: "governance",
    showInMenu: true,
    withView: true,
    isBeta: true,
    internalOnly: true,
    type: "workspace",
    hideBackButton: true,
    element: <PoliciesPage />,
    sidebarChildren: [
      { key: "policies-home-side", path: "", label: "policies.tab.home", internalOnly: true },
      { key: "policies-list-side", fullPath: "governance/politicas", label: "policies.tab.policies", internalOnly: true },
      { key: "policies-settings-side", fullPath: "governance/configuracoes", label: "policies.settings.open", internalOnly: true },
    ],
    actions: {
      view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE, VIEW_FINANCE_ROLE],
    },
    children: getPoliciesRoutes().map((route) => ({
      ...route,
      showInMenu: false,
      hideBackButton: true,
      internalOnly: true,
      type: "workspace",
      withView: true,
      actions: {
        view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE, VIEW_FINANCE_ROLE],
      },
    })),
  },
  {
    key: "chat",
    path: "chat",
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
    internalOnly: true,
    type: "workspace",
    withView: true,
    element: <ChatPage />,
  },
  {
    key: "policies-legacy-redirect",
    path: "policies/*",
    showInMenu: false,
    hideBackButton: true,
    internalOnly: true,
    type: "workspace",
    element: <LegacyPoliciesRedirect />,
    actions: {
      view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE, VIEW_FINANCE_ROLE],
    },
  },
  {
    // Stable technical redirect (not a real page): backend links target /#/ws/{workspaceId}/go/liability/:type/:id
    // (WorkspaceIdRedirect, above, resolves the /ws/{workspaceId} hop into this user's own workspace index first),
    // and this hop resolves :type/:id into whichever debts/mutuos/securitizations route currently serves that
    // contract. Never shown in the sidebar/menu.
    key: "liability-redirect",
    path: "go/liability/:type/:id",
    showInMenu: false,
    type: "workspace",
    hiddenPageTitle: true,
    hideBackButton: true,
    element: <LiabilityRedirectPage />,
    actions: {
      view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
    },
  },
];

export const SETTINGS_MENU_ITEMS = [
  {
    key: "settings",
    path: "settings",
    label: "Settings",
    element: <SettingsMenuLayout location="settings" />,
    showInMenu: true,
    hideBackButton: true,
    icon: <Settings4Line />,
    children: [
      {
        key: "profile",
        path: "profile",
        label: "Profile",
        hideBackButton: true,
        showInMenu: true,
        icon: <UserSettingsLine />,
        children: [
          {
            key: "profile-my-profile",
            icon: <UserSettingsLine />,
            label: "My profile",
            hideBackButton: true,
            path: "",
            element: <Profile />,
            parent: "settings/profile",
          },
          {
            key: "profile-security",
            label: "Security",
            path: "security",
            hideBackButton: true,
            icon: <UserSettingsLine />,
            element: <Profile />,
            parent: "settings/profile",
          },
        ],
      },
      {
        key: "settings-appearance",
        label: "Appearance",
        path: "appearance",
        element: <AppearancePage />,
        showInMenu: true,
        hideBackButton: true,
        icon: <PaletteLine />,
        parent: "settings/appearance",
      },
      {
        key: "settings-company",
        label: "Company",
        path: "company",
        element: <Company />,
        showInMenu: true,
        type: "company",
        hideBackButton: true,
        icon: <Building2Line />,
        parent: "settings/company",
      },
      {
        key: "settings-billing",
        label: "Billing",
        path: "billing",
        element: <BillingSettingsPage />,
        showInMenu: true,
        type: "company",
        taskId: 5506,
        hideBackButton: true,
        icon: <ReceiptLine />,
        parent: "settings/billing",
        actions: {
          view: [
            GENERAL_ADMIN_ROLE,
            ADMIN_ROLE,
            OPERATOR_ROLE,
            FINANCE_ROLE,
            READ_ONLY_ROLE,
            DEVELOPER_ROLE,
            VIEW_FINANCE_ROLE,
            TRANSACTIONS_ROLE,
            PAYABLES_ROLE,
            PAYMENT_CONTROLLER_ROLE,
            USER_ROLE,
          ],
        },
      },
      {
        key: "settings-general-administrators",
        icon: <Building2Line />,
        label: "General Administrators",
        path: "general-administrators",
        element: <Navigate to="../workspaces/users?scope=company" relative="path" replace />,
        showInMenu: false,
        type: "company",
        taskId: 5503,
        hideBackButton: true,
        parent: "settings/company",
      },
      {
        key: "settings-company-users",
        icon: <Building2Line />,
        label: "Users",
        path: "company-users",
        element: <Navigate to="../workspaces/users?scope=company" relative="path" replace />,
        showInMenu: false,
        type: "company",
        taskId: 5503,
        hideBackButton: true,
        parent: "settings/company",
      },
      {
        key: "settings-company-settings",
        icon: <Building2Line />,
        label: "Users",
        path: "company-settings",
        element: <Navigate to="../company" relative="path" replace />,
        showInMenu: false,
        type: "company",
        parent: "settings/company",
      },
      {
        key: "settings-notifications",
        icon: <Notification2Line />,
        label: "Notifications",
        path: "notifications",
        hideBackButton: true,
        element: <NotificationsMod />,
        showInMenu: true,
        type: "workspace",
        parent: "settings/notifications",
        children: [
          {
            key: "settings-notifications-rules",
            label: "Rules",
            path: "rules",
            element: <NotificationsMod />,
            showInMenu: false,
            icon: <Notification2Line />,
            type: "workspace",
            parent: "settings/notifications",
            actions: {
              view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
              create: [OPERATOR_ROLE, FINANCE_ROLE],
              edit: [OPERATOR_ROLE, FINANCE_ROLE],
              delete: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
            },
          },
        ],
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
          create: [OPERATOR_ROLE, FINANCE_ROLE],
          edit: [OPERATOR_ROLE, FINANCE_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE],
        },
      },
      {
        key: "settings-integrations",
        icon: <AppsLine />,
        label: "Integrations",
        path: "integrations",
        hideBackButton: true,
        element: <IntegrationsPage />,
        showInMenu: true,
        type: "workspace",
        parent: "settings/integrations",
        allowedCountries: ["BR"],
      },
      {
        key: "settings-workspaces",
        icon: <FileWord2Line />,
        label: "Workspaces",
        path: "workspaces",
        showInMenu: true,
        type: "company",
        actions: {
          view: [
            GENERAL_ADMIN_ROLE,
            ADMIN_ROLE,
            OPERATOR_ROLE,
            FINANCE_ROLE,
            READ_ONLY_ROLE,
            DEVELOPER_ROLE,
            VIEW_FINANCE_ROLE,
            TRANSACTIONS_ROLE,
            PAYABLES_ROLE,
            PAYMENT_CONTROLLER_ROLE,
            USER_ROLE,
          ],
        },
        children: [
          {
            key: "settings-workspaces-views",
            label: "Views",
            path: "views",
            showInMenu: true,
            type: "workspace",
            children: [
              {
                key: "settings-workspaces-views",
                path: "",
                element: <WorkspaceTabs />,
                showInMenu: false,
                type: "workspace",
                icon: <FileWord2Line />,
                label: "Views",
                description:
                  "Personalize the dashboard visualization, identify trends, and make strategic decisions based on accurate and up-to-date information.",
                parent: "settings/workspaces",
              },
              {
                label: "Nova visualização",
                icon: <FileWord2Line />,
                key: "settings-workspaces-views-create",
                path: "create",
                element: <WorkspaceViewActions />,
                showInMenu: false,
                type: "workspace",
                parent: "settings/workspaces",
              },
              {
                key: "settings-workspaces-users",
                label: "Users",
                path: ":workspaceId/users",
                icon: <GroupLine />,
                element: <Users />,
                showInMenu: false,
                type: "workspace",
                parent: "settings/workspaces",
              },
              {
                key: "settings-workspaces-views-mine",
                label: "My views",
                path: "mine",
                withView: true,
                element: <WorkspaceTabs />,
                showInMenu: false,
                type: "workspace",
                icon: <FileWord2Line />,
                parent: "settings/workspaces",
                actions: {
                  view: [
                    OPERATOR_ROLE,
                    FINANCE_ROLE,
                    READ_ONLY_ROLE,
                    DEVELOPER_ROLE,
                  ],
                },
              },
              {
                label: "Edit view",
                key: "settings-workspaces-views-edit",
                path: ":workspaceViewId/edit",
                icon: <GroupLine />,
                element: <WorkspaceViewActions />,
                showInMenu: false,
                type: "workspace",
              },
            ],
          },
          {
            key: "settings-workspaces",
            path: "",
            element: <WorkspaceTabs />,
            showInMenu: false,
            type: "company",
            parent: "settings/workspaces",
            description: "Check out the workspaces already available.",
            icon: <FileWord2Line />,
            label: "Workspaces",
          },
          {
            key: "settings-workspaces-mine",
            label: "My workspaces",
            path: "mine",
            withView: true,
            icon: <FileWord2Line />,
            element: <WorkspaceTabs />,
            showInMenu: false,
            type: "workspace",
            parent: "settings/workspaces",
            actions: {
              view: [
                OPERATOR_ROLE,
                FINANCE_ROLE,
                READ_ONLY_ROLE,
                DEVELOPER_ROLE,
              ],
            },
          },
          {
            key: "settings-workspaces-users",
            label: "Users",
            path: ":workspaceId/users",
            element: <Users />,
            hideBackButton: true,
            icon: <GroupLine />,
            showInMenu: false,
            type: "workspace",
            parent: "settings/workspaces",
          },
        ],
      },
      {
        key: "settings-general-owners",
        label: "Owners",
        icon: <ShareLine />,
        path: "general-owners",
        hideBackButton: true,
        showInMenu: true,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE],
          create: [OPERATOR_ROLE, FINANCE_ROLE],
          edit: [OPERATOR_ROLE, FINANCE_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE],
        },
        children: [
          {
            key: "settings-general-owners-accounts",
            path: "accounts",
            hideBackButton: true,
            element: <GeneralOwners />,
            showInMenu: false,
            type: "workspace",
            label: "Owners",
            icon: <ShareLine />,
            parent: "settings/general-owners",
            actions: {
              view: [OPERATOR_ROLE, FINANCE_ROLE],
              create: [OPERATOR_ROLE, FINANCE_ROLE],
              edit: [OPERATOR_ROLE, FINANCE_ROLE],
              delete: [OPERATOR_ROLE, FINANCE_ROLE],
            },
          },
          {
            key: "settings-general-owners",
            path: "",
            element: <GeneralOwners />,
            showInMenu: false,
            hideBackButton: true,
            type: "workspace",
            label: "Owners",
            icon: <ShareLine />,
            parent: "settings/general-owners",
            actions: {
              view: [OPERATOR_ROLE, FINANCE_ROLE],
              create: [OPERATOR_ROLE, FINANCE_ROLE],
              edit: [OPERATOR_ROLE, FINANCE_ROLE],
              delete: [OPERATOR_ROLE, FINANCE_ROLE],
            },
          },
          {
            key: "settings-general-owners",
            path: ":generalOwnerId/reassign-owners",
            element: <GeneralOwnersReassignOwners />,
            showInMenu: false,
            type: "workspace",
            label: "Reassign General Owner",
            parent: "settings/general-owners",
            icon: <ShareLine />,
            actions: {
              view: [OPERATOR_ROLE, FINANCE_ROLE],
              create: [OPERATOR_ROLE, FINANCE_ROLE],
              edit: [OPERATOR_ROLE, FINANCE_ROLE],
              delete: [OPERATOR_ROLE, FINANCE_ROLE],
            },
          },
        ],
      },
      {
        key: "settings-intercompany",
        label: "Intercompany",
        icon: <ShareLine />,
        path: "intercompany",
        hideBackButton: true,
        showInMenu: true,
        type: "workspace",
        taskId: 2300,
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
          create: [OPERATOR_ROLE, FINANCE_ROLE],
          edit: [OPERATOR_ROLE, FINANCE_ROLE],
          delete: [OPERATOR_ROLE, FINANCE_ROLE],
        },
        children: [
          {
            key: "settings-intercompany",
            path: "",
            label: "Intercompany",
            icon: <ShareLine />,
            element: <Intercompany />,
            hideBackButton: true,
            showInMenu: false,
            type: "workspace",
            parent: "settings/intercompany",
            taskId: 2300,
            actions: {
              view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
              create: [OPERATOR_ROLE, FINANCE_ROLE],
              edit: [OPERATOR_ROLE, FINANCE_ROLE],
              delete: [OPERATOR_ROLE, FINANCE_ROLE],
            },
          },
          {
            key: "settings-intercompany-companies",
            path: "companies",
            label: "Intercompany",
            icon: <ShareLine />,
            element: <Intercompany />,
            hideBackButton: true,
            showInMenu: false,
            type: "workspace",
            parent: "settings/intercompany",
            taskId: 2300,
            actions: {
              view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE],
              create: [OPERATOR_ROLE, FINANCE_ROLE],
              edit: [OPERATOR_ROLE, FINANCE_ROLE],
              delete: [OPERATOR_ROLE, FINANCE_ROLE],
            },
          },
        ],
      },
      {
        key: "settings-users",
        label: "Users",
        path: "workspaces/users",
        hideBackButton: true,
        icon: <GroupLine />,
        showInMenu: true,
        type: "workspace",
        parent: "settings/workspaces/users",
        children: [
          {
            key: "settings-users-list",
            path: "",
            element: <UsersTabs />,
            showInMenu: false,
            hideBackButton: true,
            type: "workspace",
            parent: "settings/workspaces/users",
            label: "Users",
            icon: <GroupLine />,
          },
          {
            key: "settings-users-roles-list",
            path: "roles",
            element: <UsersTabs />,
            showInMenu: false,
            hideBackButton: true,
            type: "company",
            parent: "settings/workspaces/users",
            label: "Custom roles",
            icon: <UserSettingsLine />,
          },
          {
            key: "settings-users-views-list",
            path: "views",
            element: <UsersTabs />,
            showInMenu: false,
            hideBackButton: true,
            type: "workspace",
            parent: "settings/workspaces/users",
            label: "View users",
            icon: <GroupLine />,
          },
          {
            key: "settings-users-roles-new",
            path: "roles/new",
            element: <CustomRoleForm />,
            showInMenu: false,
            type: "company",
            parent: "settings/workspaces/users",
            label: "New custom role",
            icon: <UserSettingsLine />,
          },
          {
            key: "settings-users-roles-edit",
            path: "roles/:roleId",
            element: <CustomRoleForm />,
            showInMenu: false,
            type: "company",
            parent: "settings/workspaces/users",
            label: "Edit custom role",
            icon: <UserSettingsLine />,
          },
        ],
      },
      // Legacy /settings/roles[...] redirects — kept so bookmarks and shared links keep working
      // after Custom Roles moved under /settings/workspaces/users/roles.
      {
        key: "settings-custom-roles-legacy",
        label: "Custom roles",
        path: "roles",
        showInMenu: false,
        hideBackButton: true,
        type: "company",
        parent: "settings/workspaces/users",
        children: [
          {
            key: "settings-custom-roles-legacy-list",
            path: "",
            element: <LegacyRolesRedirect />,
            showInMenu: false,
            type: "company",
            parent: "settings/workspaces/users",
            label: "Custom roles",
          },
          {
            key: "settings-custom-roles-legacy-new",
            path: "new",
            element: <LegacyRolesRedirect suffix="new" />,
            showInMenu: false,
            type: "company",
            parent: "settings/workspaces/users",
            label: "New custom role",
          },
          {
            key: "settings-custom-roles-legacy-edit",
            path: ":roleId",
            element: <LegacyRolesRedirect />,
            showInMenu: false,
            type: "company",
            parent: "settings/workspaces/users",
            label: "Edit custom role",
          },
        ],
      },
      {
        key: "settings-security",
        icon: <ShieldUserLine />,
        label: "Security",
        path: "password-policy",
        element: <Security />,
        showInMenu: false,
        type: "company",
        parent: "settings/login-logs",
      },
      {
        key: "user-login-logs",
        icon: <ShieldUserLine />,
        label: "Security",
        path: "login-logs",
        element: <Security />,
        showInMenu: true,
        type: "company",
        taskId: 5504,
        parent: "settings/login-logs",
      },
      {
        key: "settings-bank-integrations",
        icon: <BankIntegrationCredentialIcon />,
        label: "Bank Credentials",
        description: "Your credentials are encrypted and stored securely.",
        path: "bank-integrations",
        element: <BankIntegrationsPage />,
        hideBackButton: true,
        showInMenu: true,
        type: "workspace",
        taskId: 2002,
        parent: "settings/bank-integrations",
      },
      {
        key: "developers",
        label: "Developers",
        icon: <CodeSSlashFill />,
        path: "developers",
        showInMenu: true,
        hideBackButton: true,
        type: "workspace",
        actions: {
          view: [OPERATOR_ROLE, FINANCE_ROLE, READ_ONLY_ROLE, VIEW_FINANCE_ROLE, DEVELOPER_ROLE],
        },
        children: [
          {
            key: "developers-index",
            path: "",
            element: <Navigate to="api-keys" replace />,
          },
          {
            key: "api-activity",
            icon: <PulseLine />,
            label: "API Activity",
            path: "api-activity",
            element: <Home />,
            showInMenu: false,
            type: "workspace",
            parent: "settings/developers",
            actions: {
              view: [DEVELOPER_ROLE],
              create: [DEVELOPER_ROLE],
              edit: [DEVELOPER_ROLE],
              delete: [DEVELOPER_ROLE],
            },
          },
          {
            key: "api-keys",
            label: "API Keys",
            description: "Generate up to 5 API Keys.",
            icon: <KeyLine />,
            hideBackButton: true,
            path: "api-keys",
            element: <DevelopersTabs />,
            showInMenu: true,
            type: "workspace",
            taskId: 5505,
            parent: "settings/developers",
            actions: {
              view: [DEVELOPER_ROLE],
              create: [DEVELOPER_ROLE],
              edit: [DEVELOPER_ROLE],
              delete: [DEVELOPER_ROLE],
            },
          },
          {
            key: "api-logs",
            icon: <FileHistoryLine />,
            label: "API Logs",
            description: "Authorized IPs",
            path: "api-logs",
            element: <Logs />,
            showInMenu: false,
            type: "workspace",
            parent: "settings/developers",
            actions: {
              view: [DEVELOPER_ROLE],
            },
          },
          {
            key: "ip-whitelist",
            icon: <MapPin2Line />,
            label: "IP Whitelist",
            path: "ip-whitelist",
            hideBackButton: true,
            element: <DevelopersTabs />,
            showInMenu: true,
            type: "workspace",
            taskId: 6101,
            parent: "settings/developers",
            actions: {
              view: [DEVELOPER_ROLE],
              create: [DEVELOPER_ROLE],
              edit: [DEVELOPER_ROLE],
              delete: [DEVELOPER_ROLE],
            },
          },
        ],
      },
      {
        key: "mcp-management",
        label: "Gestão MCP",
        icon: <ShareLine />,
        path: "mcp",
        element: <McpManagement />,
        showInMenu: true,
        hideBackButton: true,
        type: "workspace",
        taskId: 5505,
        parent: "settings/mcp",
        actions: {
          view: [DEVELOPER_ROLE],
          create: [DEVELOPER_ROLE],
          edit: [DEVELOPER_ROLE],
          delete: [DEVELOPER_ROLE],
        },
      },
    ],
  },
];

export const APP_ROUTES = [
  {
    path: "/:workspace",
    children: [
      ...MAIN_MENU_ITEMS,
      ...SETTINGS_MENU_ITEMS,
      ...ONBOARDING_MENU_ITEMS,
    ],
  },
];
